/**
 * SQL を読んで「何が起きるか」を利用者の言葉で言うための純関数（import なし = node --test でも MCP のテストからでもそのまま読める）。
 * 2026-10-04 console/plain-confirm-2。
 *
 * WHY（founder 2026-10-04）: 以前の確認の画面は「何を実行するかは『くわしく見る』で確かめてください」と SQL に投げていた。
 *   「エンジニアでない人は SQL を読めない。判断に要ることを『くわしく見る』に投げない」。
 *   → 文の種類（SELECT / INSERT / UPDATE / DELETE / DROP…）と表の名前だけを読み、結果の 1 行を作る（confirmPlain.ts）。
 *
 * 読み方の決まり（安全側に倒す = 自信がなければ 'unknown'。「何も変わりません」と嘘をつく方が、わからないと言うより悪い）:
 *   - コメント（-- / / * * /）と文字列（'…'）は先に消す = 中の「DROP TABLE」などの SQL らしい言葉にだまされない
 *   - 閉じていないコメント・文字列、知らない文（PRAGMA・ATTACH・VACUUM…）は 'unknown'
 *   - 本体は SQL を先頭 1000 文字で切って渡す（server pendingActionKinds.ts の db_query）。1000 文字以上 = 後ろが見えない = 'unknown'
 *   - ; で文を分け、文ごとに種類を決めて、いちばん強いもの 1 つで言う（read < schema < insert < update < delete < dropColumn < drop）
 * D1（SQLite）が前提。WITH … の中身（CTE）は SELECT しか書けない = WITH の文で INSERT / UPDATE / DELETE / REPLACE INTO が出たら、それが本体の文。
 *
 * 見出しとボタンの名前も、ここで読んだ種類で決める（2026-10-04 管理: DELETE なのに「書き換えますか？」「書き換える」は結果の行と食い違う）:
 *   消す（delete / dropColumn / drop）・追加する（insert）・書き換える（update と unknown）・実行する（read）・変更する（schema）。
 * MCP（apps/mcp/src/pendingActions.ts sqlKindOf）もボタンの名前に同じ判断が要る = 同じ読み方の写し（MCP はダッシュボードのファイルを読み込めない）。
 * MCP のテスト（pendingLink.test.ts）がこのファイルを読み込んで、両方の答えが同じであることを確かめる。
 */

/**
 * 文の種類（弱い順）。'schema' = 表を作る・列を足す・索引を消すなど、保存済みのデータは残る変更。
 * 'dropColumn' = ALTER TABLE … DROP COLUMN（表は残るが、その項目の中身が全部の行で消える。「表を中身ごと消す」と言うと大げさで嘘になる）
 */
export type SqlKind = 'read' | 'schema' | 'insert' | 'update' | 'delete' | 'dropColumn' | 'drop';
const RANK: readonly SqlKind[] = ['read', 'schema', 'insert', 'update', 'delete', 'dropColumn', 'drop'];

export type SqlEffect =
  | { kind: 'unknown' }
  | {
      kind: SqlKind;
      /** いちばん強い種類の文が触る表（SQL に書かれたまま・最大 3 つ） */
      tables: string[];
      /** いちばん強いもの以外の変更（読むだけは数えない）がある、または表が 3 つより多い = 「ほかの変更もあります」 */
      more: boolean;
    };

/** 本体が db_query の SQL を切る長さ（server/src/lib/pendingActionKinds.ts と同じ）。これ以上 = 後ろが切れているかもしれない */
export const SQL_SHOWN_MAX = 1000;
const MAX_TABLES = 3;

type Tok = { w: string; ident?: boolean };

/** コメントと文字列を消して、語（大文字にした単語・名前）と記号に分ける。閉じていない物があれば null */
function tokenize(sql: string): Tok[] | null {
  const out: Tok[] = [];
  let i = 0;
  while (i < sql.length) {
    const c = sql[i]!;
    if (/\s/.test(c)) {
      i++;
    } else if (c === '-' && sql[i + 1] === '-') {
      const end = sql.indexOf('\n', i);
      i = end < 0 ? sql.length : end + 1;
    } else if (c === '/' && sql[i + 1] === '*') {
      const end = sql.indexOf('*/', i + 2);
      if (end < 0) return null;
      i = end + 2;
    } else if (c === "'") {
      // '' は文字列の中の ' 。値は捨てる（中の言葉は SQL ではない）
      let j = i + 1;
      for (;;) {
        if (j >= sql.length) return null;
        if (sql[j] === "'") {
          if (sql[j + 1] === "'") j += 2;
          else break;
        } else j++;
      }
      out.push({ w: "'" });
      i = j + 1;
    } else if (c === '"' || c === '`' || c === '[') {
      // 囲んだ名前（"my table" / `t` / [t]）= 表の名前になりうる。中身はそのまま名前として残す
      const close = c === '[' ? ']' : c;
      const end = sql.indexOf(close, i + 1);
      if (end < 0) return null;
      out.push({ w: sql.slice(i + 1, end), ident: true });
      i = end + 1;
    } else if (/[A-Za-z_]/.test(c)) {
      let j = i + 1;
      while (j < sql.length && /[A-Za-z0-9_$]/.test(sql[j]!)) j++;
      out.push({ w: sql.slice(i, j) });
      i = j;
    } else {
      out.push({ w: c });
      i++;
    }
  }
  return out;
}

const up = (t: Tok | undefined) => (t && !t.ident ? t.w.toUpperCase() : '');

/** 位置 p から表の名前（schema.table も書かれたまま）。名前でなければ null */
function tableAt(toks: Tok[], p: number): string | null {
  const isName = (t: Tok | undefined) => !!t && (t.ident || /^[A-Za-z_]/.test(t.w));
  if (!isName(toks[p])) return null;
  let name = toks[p]!.w;
  if (toks[p + 1]?.w === '.' && isName(toks[p + 2])) name += '.' + toks[p + 2]!.w;
  return name.slice(0, 64);
}

/** 位置 p の後の IF [NOT] EXISTS を飛ばす */
function skipIfExists(toks: Tok[], p: number): number {
  if (up(toks[p]) !== 'IF') return p;
  if (up(toks[p + 1]) === 'NOT') p++;
  return up(toks[p + 1]) === 'EXISTS' ? p + 2 : p;
}

/** 1 つの文の種類と表。読めなければ null。トランザクションの文（BEGIN / COMMIT…）は 'skip' */
function classifyStatement(toks: Tok[]): { kind: SqlKind; table: string | null } | 'skip' | null {
  let p = 0;
  let head = up(toks[0]);
  if (head === 'WITH') {
    // CTE の中は SELECT だけ（SQLite）= 本体の文は最初に出る INSERT / UPDATE / DELETE / REPLACE INTO。無ければ SELECT（読むだけ）
    const at = toks.findIndex((t, k) => {
      const u = up(t);
      return u === 'INSERT' || u === 'UPDATE' || u === 'DELETE' || (u === 'REPLACE' && up(toks[k + 1]) === 'INTO');
    });
    if (at < 0) return { kind: 'read', table: null };
    p = at;
    head = up(toks[at]);
  }
  switch (head) {
    case 'SELECT':
    case 'VALUES':
      return { kind: 'read', table: null };
    case 'EXPLAIN':
      return { kind: 'read', table: null };
    case 'BEGIN':
    case 'COMMIT':
    case 'END':
    case 'ROLLBACK':
    case 'SAVEPOINT':
    case 'RELEASE':
      return 'skip';
    case 'INSERT':
    case 'REPLACE': {
      // INSERT [OR x] INTO t / REPLACE INTO t
      const into = toks.findIndex((t, k) => k > p && up(t) === 'INTO');
      return into < 0 ? null : { kind: 'insert', table: tableAt(toks, into + 1) };
    }
    case 'UPDATE': {
      let q = p + 1;
      if (up(toks[q]) === 'OR') q += 2;
      return { kind: 'update', table: tableAt(toks, q) };
    }
    case 'DELETE':
      return up(toks[p + 1]) === 'FROM' ? { kind: 'delete', table: tableAt(toks, p + 2) } : null;
    case 'TRUNCATE': {
      const q = up(toks[p + 1]) === 'TABLE' ? p + 2 : p + 1;
      return { kind: 'drop', table: tableAt(toks, q) };
    }
    case 'DROP': {
      const what = up(toks[p + 1]);
      if (what === 'TABLE') return { kind: 'drop', table: tableAt(toks, skipIfExists(toks, p + 2)) };
      // 索引・ビュー・トリガーを消しても、保存したデータは残る
      if (what === 'INDEX' || what === 'VIEW' || what === 'TRIGGER') return { kind: 'schema', table: null };
      return null;
    }
    case 'CREATE': {
      let q = p + 1;
      if (up(toks[q]) === 'TEMP' || up(toks[q]) === 'TEMPORARY') q++;
      if (up(toks[q]) === 'UNIQUE') q++;
      const what = up(toks[q]);
      if (what === 'TABLE') return { kind: 'schema', table: tableAt(toks, skipIfExists(toks, q + 1)) };
      if (what === 'INDEX' || what === 'VIEW' || what === 'TRIGGER' || what === 'VIRTUAL') return { kind: 'schema', table: null };
      return null;
    }
    case 'ALTER': {
      if (up(toks[p + 1]) !== 'TABLE') return null;
      const table = tableAt(toks, p + 2);
      // ALTER TABLE t DROP [COLUMN] c = その列のデータが消える。ADD / RENAME はデータが残る
      const rest = toks.slice(p + 3).map(up);
      if (rest.includes('DROP')) return { kind: 'dropColumn', table };
      if (rest.includes('ADD') || rest.includes('RENAME')) return { kind: 'schema', table };
      return null;
    }
    default:
      return null;
  }
}

/** SQL（本体が渡した文字列）から、起きることを読む。読めない・切れているかもしれない時は { kind: 'unknown' } */
export function sqlEffect(sql: unknown): SqlEffect {
  if (typeof sql !== 'string' || sql.length >= SQL_SHOWN_MAX) return { kind: 'unknown' };
  const toks = tokenize(sql);
  if (!toks) return { kind: 'unknown' };
  const stmts: Tok[][] = [[]];
  for (const t of toks) {
    if (!t.ident && t.w === ';') stmts.push([]);
    else stmts[stmts.length - 1]!.push(t);
  }
  const found: Array<{ kind: SqlKind; table: string | null }> = [];
  for (const s of stmts) {
    if (s.length === 0) continue;
    const c = classifyStatement(s);
    if (c === null) return { kind: 'unknown' };
    // データを変える文で表の名前が読めない = 「どこが変わるか」を言えない = unknown（見出し・ボタン・結果の行がそろって「判断できません」側になる。
    //   MCP のボタンの名前も同じ判断 = apps/mcp/src/pendingActions.ts sqlKindOf）
    if (c !== 'skip' && c.table === null && (c.kind === 'insert' || c.kind === 'update' || c.kind === 'delete' || c.kind === 'dropColumn' || c.kind === 'drop')) return { kind: 'unknown' };
    if (c !== 'skip') found.push(c);
  }
  if (found.length === 0) return { kind: 'unknown' };
  const top = found.reduce((a, b) => (RANK.indexOf(b.kind) > RANK.indexOf(a.kind) ? b : a)).kind;
  const all = [...new Set(found.filter((f) => f.kind === top && f.table).map((f) => f.table!))];
  const others = found.some((f) => f.kind !== top && f.kind !== 'read');
  return { kind: top, tables: all.slice(0, MAX_TABLES), more: others || all.length > MAX_TABLES };
}

/** 種類だけ（見出しとボタンの名前を決める。MCP の sqlKindOf と同じ答え = MCP のテストが突き合わせる） */
export function sqlKind(sql: unknown): SqlKind | 'unknown' {
  return sqlEffect(sql).kind;
}
